<template>
    <div class="ribbon-wrap" aria-hidden="true">
        <div class="noise-layer"></div>
        <div ref="blob1" class="blob blob-1"></div>
        <div ref="blob2" class="blob blob-2"></div>
        <div ref="blob3" class="blob blob-3"></div>
        <div ref="blob4" class="blob blob-4"></div>
        <div class="noise-overlay"></div>
    </div>
</template>

<script setup lang="ts">
import gsap from "gsap";
import { onMounted, onUnmounted, useTemplateRef } from "vue";

const blob1 = useTemplateRef<HTMLElement>("blob1");
const blob2 = useTemplateRef<HTMLElement>("blob2");
const blob3 = useTemplateRef<HTMLElement>("blob3");
const blob4 = useTemplateRef<HTMLElement>("blob4");

let ctx: gsap.Context | null = null;

function morphBlob(el: HTMLElement, duration: number, delay: number) {
    const shapes = [
        "30% 70% 70% 30% / 30% 30% 70% 70%",
        "60% 40% 30% 70% / 50% 60% 40% 50%",
        "40% 60% 50% 50% / 70% 30% 60% 40%",
        "70% 30% 40% 60% / 40% 70% 30% 60%",
        "50% 50% 60% 40% / 30% 50% 50% 70%",
    ];

    const tl = gsap.timeline({ repeat: -1, delay });

    for (let i = 0; i < shapes.length; i++) {
        const next = shapes[(i + 1) % shapes.length];
        tl.to(el, {
            borderRadius: next,
            duration,
            ease: "sine.inOut",
        });
    }

    gsap.to(el, {
        x: "random(-50, 50)",
        y: "random(-40, 40)",
        rotation: "random(-12, 12)",
        duration: duration * 1.5,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
        delay,
    });

    gsap.to(el, {
        scale: "random(0.9, 1.15)",
        duration: duration * 1.5,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
        delay: delay + 1,
    });
}

onMounted(() => {
    const prefersReduced = globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
        return;
    }

    const b1 = blob1.value;
    const b2 = blob2.value;
    const b3 = blob3.value;
    const b4 = blob4.value;
    if (b1 && b2 && b3 && b4) {
        ctx = gsap.context(() => {
            morphBlob(b1, 3, 0);
            morphBlob(b2, 4, 0.5);
            morphBlob(b3, 3.5, 0.25);
            morphBlob(b4, 4.5, 0.75);
        });
    }
});

onUnmounted(() => {
    ctx?.revert();
});
</script>

<style scoped>
.ribbon-wrap {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
    z-index: 0;
    background: linear-gradient(160deg, #f0eeff 0%, #e8e4ff 40%, #ece6fb 100%);
}

.blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(25px);
}

/* Large primary blob */
.blob-1 {
    width: 70%;
    height: 70%;
    top: -10%;
    right: -15%;
    background: radial-gradient(ellipse at 40% 40%, rgba(85, 91, 162, 0.5) 0%, rgba(139, 92, 246, 0.3) 50%, transparent 80%);
    border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
}

/* Bright violet accent */
.blob-2 {
    width: 55%;
    height: 55%;
    bottom: -5%;
    left: -10%;
    background: radial-gradient(ellipse at 60% 50%, rgba(188, 99, 232, 0.45) 0%, rgba(158, 0, 220, 0.2) 50%, transparent 80%);
    border-radius: 60% 40% 30% 70% / 50% 60% 40% 50%;
}

/* Soft light bloom */
.blob-3 {
    width: 50%;
    height: 50%;
    top: 30%;
    left: 25%;
    background: radial-gradient(circle at 50% 50%, rgba(192, 192, 255, 0.5) 0%, rgba(184, 201, 255, 0.25) 50%, transparent 75%);
    border-radius: 40% 60% 50% 50% / 70% 30% 60% 40%;
}

/* Small warm accent */
.blob-4 {
    width: 35%;
    height: 35%;
    top: 5%;
    left: 10%;
    background: radial-gradient(circle at 50% 50%, rgba(226, 189, 244, 0.5) 0%, rgba(210, 160, 240, 0.2) 50%, transparent 75%);
    border-radius: 70% 30% 40% 60% / 40% 70% 30% 60%;
}

/* SVG noise texture — generated inline as a data URI for zero network requests */
.noise-overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
    opacity: 0.5;
    mix-blend-mode: overlay;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    background-repeat: repeat;
    background-size: 180px 180px;
    pointer-events: none;
}
</style>
